import React, { useState } from "react";
import tw from "twin.macro";
import styled from "styled-components";
import { SectionHeading, Subheading as SubheadingBase } from "components/misc/Headings.js";
import { PrimaryButton as PrimaryButtonBase } from "components/misc/Buttons.js";
import EmailIllustrationSrc from "images/enquiry.svg";
import { CountryDropdown } from 'react-country-region-selector'; // Install package 'react-country-region-selector'
import { FaCheckCircle, FaTimesCircle, FaInfoCircle } from 'react-icons/fa';

const API_URL = process.env.REACT_APP_IS_PROD == "true" ? process.env.REACT_APP_API_URL_PROD : process.env.REACT_APP_API_URL_DEV


const Container = tw.div`relative`;
const TwoColumn = tw.div`flex flex-col md:flex-row justify-between max-w-screen-xl mx-auto`;
const Column = tw.div`w-full max-w-md mx-auto md:max-w-none md:mx-0`;
const ImageColumn = tw(Column)`md:w-5/12 flex-shrink-0 h-80 md:h-auto`;
const TextColumn = styled(Column)(props => [
  tw`md:w-7/12 mt-16 md:mt-0`,
  props.textOnLeft ? tw`md:mr-12 lg:mr-16 md:order-first` : tw`md:ml-12 lg:ml-16 md:order-last`
]);

const Image = styled.div(props => [
  `background-image: url("${props.imageSrc}");`,
  tw`rounded bg-contain bg-no-repeat bg-center h-full`
]);
const TextContent = tw.div`lg:py-8 text-center md:text-left`;

const Subheading = tw(SubheadingBase)`text-center md:text-left text-4xl`;
const Heading = tw(SectionHeading)`mt-4 font-black text-left text-3xl sm:text-4xl lg:text-5xl text-center md:text-left leading-tight`;
const Description = tw.p`mt-4 text-center md:text-left text-sm md:text-base lg:text-lg font-medium leading-relaxed text-secondary-100`

const Form = tw.form`mt-8 md:mt-10 text-sm flex flex-col max-w-sm mx-auto md:mx-0`
const Input = tw.input`mt-6 first:mt-0 border-b-2 py-3 focus:outline-none font-medium transition duration-300 hocus:border-[#0049AC] w-full`;
const Textarea = styled(Input).attrs({ as: "textarea" })`
  ${tw`h-12`}
`;

const SubmitButton = tw(PrimaryButtonBase)`inline-block mt-8 w-full flex justify-center items-center`;

const ProgressCircle = tw.div`w-10 h-10 border-4 border-t-4 border-[#0049AC] rounded-full animate-spin`;

const DialogBox = styled.div`
  ${tw`fixed top-0 left-0 w-full h-full flex justify-center items-center bg-gray-500 bg-opacity-50 z-50`}
  display: ${({ show }) => (show ? "flex" : "none")};
  animation: fadeIn 0.3s ease-in-out;
  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const DialogContent = tw.div`bg-white p-6 rounded-lg shadow-xl text-center w-[90%] max-w-md`;
const DialogHeading = tw.h2`text-xl font-bold text-gray-900`;
const DialogMessage = tw.p`mt-4 text-sm text-gray-700`;
const OkButton = tw.button`mt-4 px-4 py-2 bg-[#0049AC] text-white rounded-full`;

export default ({ subheading = "", heading = <>Feel free to <span tw="text-[#0049AC]">get in touch</span><wbr /> with us.</>, description = "Reach out to us with any questions about our packages, accommodations, travel itineraries, or special requirements.", submitButtonText = "Submit", formAction = "#", formMethod = "get", textOnLeft = true }) => {
  const [formData, setFormData] = useState({ email: "", mobile: "", name: "", message: "", countryCode: "IN" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const [message, setMessage] = useState('');
  const [fieldError, setFieldError] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleCountryChange = (value) => {
    setFormData({ ...formData, countryCode: value });
  };

  const validateForm = () => {
    if (!/^[\w-]+(\.[\w-]+)*@([\w-]+\.)+[a-zA-Z]{2,7}$/.test(formData.email)) {
      setFieldError(true);
      setIsSuccess(false);
      setShowDialog(true);
      setMessage('Please input correct email.');
      return false;
    }
    if (!/^\d{10}$/.test(formData.mobile)) {
      setFieldError(true);
      setIsSuccess(false);
      setShowDialog(true);
      setMessage('Please input correct mobile number.');
      return false;
    }
    if (formData.countryCode == null || formData.countryCode == undefined) {
      setFieldError(true);
      setIsSuccess(false);
      setShowDialog(true);
      setMessage('Please choose country name.');
      return false;
    }
    if (!formData.email || !formData.mobile || !formData.name || !formData.message) {
      setFieldError(true);
      setIsSuccess(false);
      setShowDialog(true);
      setMessage('Something is missing, Please fill enquiry details.');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/web/home/enquiry`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          'X-API-Key': process.env.REACT_APP_ACCESS_API_KEY // Add API key to headers
        },
        body: JSON.stringify({
          ...formData,
          ip_address: ""
        }),
      });

      const result = await response.json();

      if (result.header.resCode = 200 && result.data.length > 0) {
        setIsSuccess(true);
        setFormData({ email: "", mobile: "", name: "", message: "", countryCode: "IN" });
      } else {
        setIsSuccess(false);
      }
    } catch (error) {
      setIsSuccess(false);
      setMessage('')
      setFieldError(false)
    } finally {
      setIsSubmitting(false);
      setShowDialog(true);
    }
  };

  const handleCloseDialog = () => {
    setShowDialog(false);
    setMessage('')
    setFieldError(false)
  };

  return (
    <Container>
      <TwoColumn>
        <ImageColumn>
          <Image imageSrc={EmailIllustrationSrc} />
        </ImageColumn>
        <TextColumn textOnLeft={textOnLeft}>
          <TextContent>
            {subheading && <Subheading>{subheading}</Subheading>}
            <Heading>Feel free to <span tw="text-[#0049AC]!" className="textColorHeading">get in touch</span> with us.</Heading>
            {description && <Description>{description}</Description>}
            <Form onSubmit={handleSubmit}>
              <Input type="email" name="email" placeholder="Your Email Address" value={formData.email} onChange={handleInputChange} />

              {/* Mobile Number and Country Code Dropdown in the same line */}
              <div tw="flex items-center mt-6">
                {/* Country Dropdown Styled as Placeholder */}
                <div tw="w-1/4 relative">
                  <br />
                  <CountryDropdown
                    value={formData.countryCode}
                    onChange={handleCountryChange}
                    defaultOptionLabel="India"
                    tw="w-full h-12 px-4 border-b-2 focus:outline-none text-sm font-medium transition duration-300 hocus:border-[#0049AC]"
                    style={{
                      backgroundColor: "transparent",
                      border: "none",
                      borderBottom: "2px solid #e2e8f0",
                      padding: "8px 0",
                      fontSize: "14px",
                      color: formData.countryCode === "Select Country" ? "#a0aec0" : "#4a5568", // Placeholder color vs selected text color
                      width: "100%",
                      cursor: "pointer",
                    }}
                  />
                </div>
                {/* Mobile Number Input */}
                <Input
                  type="text"
                  name="mobile"
                  placeholder="Mobile Number"
                  value={formData.mobile}
                  onChange={handleInputChange}
                  tw="w-3/4 ml-4"
                />
              </div>

              <Input type="text" name="name" placeholder="Name" value={formData.name} onChange={handleInputChange} />
              <Textarea name="message" placeholder="What's in your mind? Ask us." value={formData.message} onChange={handleInputChange} />
              <SubmitButton type="submit" disabled={isSubmitting}>
                {isSubmitting ? <ProgressCircle /> : submitButtonText}
              </SubmitButton>
            </Form>
          </TextContent>
        </TextColumn>
      </TwoColumn>

      <DialogBox show={showDialog}>
        <DialogContent>
          <DialogHeading className="dialog-heading">
            {isSuccess ? (
              <FaCheckCircle className="success-icon" />
            ) : fieldError ? (
              <FaInfoCircle className="info-icon" />
            ) : (
              <FaTimesCircle className="error-icon" />
            )}
          </DialogHeading>
          <DialogMessage>
            {isSuccess ? "Enquiry Submitted, Our Executive will connect with you." : fieldError ? message : "There was an issue submitting your enquiry. Please try again."}
          </DialogMessage>
          <OkButton onClick={handleCloseDialog}>OK</OkButton>
        </DialogContent>
      </DialogBox>

      <br /><br />
    </Container>
  );
};
